import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Card, CardContent, CircularProgress, Grid, Stack, Typography } from '@mui/material'
import { useOutletContext, useParams } from 'react-router'
import { groupBy, sortBy, uniqBy } from 'lodash'
import moment from 'moment'
import { toast } from 'react-toastify'
import useTitles from 'common/hooks/useTitles'
import useActions from 'common/hooks/useActions'
import { OTHER, REFRESH } from 'common/components/action/ActionConstant'
import ApplicationConf from 'conf/ApplicationConf'
import { aquaFetch } from 'common/utils/ActionUtils'
import { CardTable, SORT } from 'common/components/datatable/Table'
import { CardTitle } from 'common/components/card/Card'
import Input from 'common/components/form/Input'
import Select from 'common/components/form/Select'
import { darkBlue } from 'app/theme'
import TabList from 'common/components/tab/TabList'
import { getDate } from 'common/utils/DateUtil'
import { truncateString } from 'common/utils/StringUtil'
import { STATUS } from 'common/constants/storeConstants'
import { CATEGORIES_LABELS, STATUS_TICKETS, STATUS_TICKETS_LABEL } from 'common/constants/Constants'
import { getColorDueDate } from 'common/utils/ColorUtil'

const TAB_TYPE = 'TYPE'
const TAB_STATUS = 'ETAT'
const TAB_YEAR = 'ANNEE'

// Statuts « ouverts » : masquer les tickets fermés = ne garder que ceux-là (aligné sur Suivi des lots).
const OPEN_STATUS = [
    STATUS_TICKETS_LABEL.NOUVEAU,
    STATUS_TICKETS_LABEL.AFFECTE,
    STATUS_TICKETS_LABEL.COMMENTAIRE,
    STATUS_TICKETS_LABEL.EN_COURS,
    STATUS_TICKETS_LABEL.REOUVERT,
]

export const TICKET_HEADERS = [
    { key: 'id', value: 'Id' },
    { key: 'summary', value: 'Titre' },
    { key: 'status', value: 'Statut' },
    { key: 'category', value: 'Catégorie' },
    { key: 'dateSubmitted', value: 'Date de création' },
    { key: 'echeance', value: 'Échéance' },
    { key: 'reporter', value: 'Rapporteur' },
    { key: 'handler', value: 'Affecté à' },
    { key: 'tempspasse', value: 'Temps passé (h)' },
]

const statusColor = (label) => STATUS_TICKETS.find(s => s.value === label)?.color

export const formatRow = (t) => ({
    id: t.id,
    summary: { value: truncateString(t.summary, 60), sortValue: t.summary, tooltip: t.summary?.length > 60 ? t.summary : undefined },
    status: { value: t.status, color: statusColor(t.status) },
    category: { value: t.category },
    dateSubmitted: { value: getDate(t.dateSubmitted * 1000), sortValue: t.dateSubmitted },
    echeance: {
        value: t.dueDate && t.dueDate > 1 ? getDate(t.dueDate * 1000) : '',
        sortValue: t.dueDate ?? -1,
        color: t.dueDate && t.dueDate !== 1 && ![STATUS_TICKETS_LABEL.EN_RECETTE, STATUS_TICKETS_LABEL.TRAITE, STATUS_TICKETS_LABEL.FERME].includes(t.status) ? getColorDueDate(t.dueDate * 1000) : 'white',
    },
    reporter: { value: t.reporter },
    handler: { value: t.handler },
    tempspasse: { value: t.tempspasse ?? '', sortValue: Number(t.tempspasse) || 0 },
})

const FilterTickets = ({ tickets, filter, setFilter }) => {
    const { searchValue = '', category = [], status = [], reporter = [], handler = [] } = filter
    const options = (key) => sortBy(uniqBy(tickets.filter(t => t[key]), key).map(t => ({ value: t[key], label: t[key] })), 'value')
    return (
        <Card sx={{ marginRight: 1, marginBottom: 1 }}>
            <CardTitle title='Filtre' color={darkBlue} />
            <CardContent>
                <Grid container spacing={1} alignItems='center'>
                    <Grid size={4}>
                        <Input label='Rechercher' value={searchValue} onChange={v => setFilter({ ...filter, searchValue: v })} />
                    </Grid>
                    <Grid size={4}>
                        <Select multiple label='Catégorie' value={category} items={CATEGORIES_LABELS.map(c => ({ value: c, label: c }))} onChange={v => setFilter({ ...filter, category: v })} undefinedValue />
                    </Grid>
                    <Grid size={4}>
                        <Select multiple label='Statut' value={status} items={STATUS_TICKETS.map(s => ({ value: s.value, label: s.value }))} sortItems={false} onChange={v => setFilter({ ...filter, status: v })} undefinedValue />
                    </Grid>
                    <Grid size={4}>
                        <Select multiple label='Rapporteur' value={reporter} items={options('reporter')} onChange={v => setFilter({ ...filter, reporter: v })} undefinedValue />
                    </Grid>
                    <Grid size={4}>
                        <Select multiple label='Affecté à' value={handler} items={options('handler')} onChange={v => setFilter({ ...filter, handler: v })} undefinedValue />
                    </Grid>
                    <Grid container size={4} justifyContent='flex-end' alignItems='center'>
                        <Button variant='outlined' onClick={() => setFilter({})}>Réinitialiser</Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

FilterTickets.propTypes = {
    tickets: PropTypes.arrayOf(PropTypes.shape({})),
    filter: PropTypes.object,
    setFilter: PropTypes.func,
}

// Rend une CardTable par groupe (Type / État / Année), triées selon la dimension.
const GroupedTables = ({ tickets, keyOf, sortKeys, labelOf }) => {
    const groups = groupBy(tickets, keyOf)
    const keys = sortKeys(Object.keys(groups))
    if (!keys.length) {
        return <Typography color='textSecondary'>Aucun ticket.</Typography>
    }
    return (
        <Stack spacing={2}>
            {keys.map(key => (
                <CardTable
                    key={key}
                    title={labelOf(key)}
                    color={darkBlue}
                    headers={TICKET_HEADERS}
                    rows={groups[key].map(formatRow)}
                    rowsPerPageDefault={25}
                    defaultSort={{ column: 'dateSubmitted', direction: SORT.DESC }}
                    onClickRow={({ id }) => window.open(`https://support.aquasys.fr/view.php?id=${id}`, '_blank')}
                />
            ))}
        </Stack>
    )
}

GroupedTables.propTypes = {
    tickets: PropTypes.arrayOf(PropTypes.shape({})),
    keyOf: PropTypes.func,
    sortKeys: PropTypes.func,
    labelOf: PropTypes.func,
}

const CustomerTicketsApp = () => {
    const { id } = useParams()
    const { customer, projects = [] } = useOutletContext()

    const [instancesParams, setInstancesParams] = useState([])
    const [tickets, setTickets] = useState([])
    const [status, setStatus] = useState(STATUS.IDLE)
    const [filter, setFilter] = useState({})
    const [hideClosed, setHideClosed] = useState(true)

    useTitles(() => [{
        label: 'Clients',
        href: '/customer',
    }, {
        label: customer?.name ?? 'Inconnu',
        href: `/customer/${id}`,
    }, {
        label: 'Tickets',
        href: `/customer/${id}/tickets`,
    }], [id, customer])

    // Projet(s) Mantis du client : paramètre MANTIS_NAME des instances (mêmes IDs que Prestations).
    useEffect(() => {
        setInstancesParams([])
        customer?.instances?.forEach(i => {
            aquaFetch(ApplicationConf.instance.single(i.instance)).then(inst => setInstancesParams(prev => [...prev, ...(inst?.parameters ?? [])]))
        })
    }, [customer])

    const projectIds = useMemo(() => instancesParams.find(p => p.parameter === 'MANTIS_NAME')?.value?.split(',').map(v => Number(v)), [instancesParams])
    const projectNames = useMemo(() => (projectIds ? projects.filter(p => projectIds.includes(p.id)).map(p => p.name) : []), [projects, projectIds])

    const reload = useCallback(() => {
        if (!projectNames.length) {
            return
        }
        setStatus(STATUS.LOADING)
        aquaFetch(ApplicationConf.support.statisticsCreated(), { method: 'POST', body: { projects: projectNames } })
            .then(res => {
                setTickets(res ?? [])
                setStatus(STATUS.SUCCEEDED)
            })
            .catch(() => {
                setStatus(STATUS.REJECTED)
                toast.error('Erreur lors du chargement des tickets')
            })
    }, [projectNames])

    useEffect(() => {
        if (!customer || !instancesParams.length || !projects.length) {
            return
        }
        if (!projectIds) {
            toast.error('Paramètre manquant sur l\'instance : MANTIS_NAME')
            return
        }
        reload()
    }, [customer, projects, instancesParams, projectIds, reload])

    useActions(() => [{
        type: REFRESH,
        onClick: reload,
        loading: status === STATUS.LOADING,
    }, {
        type: OTHER,
        icon: 'flaky',
        tooltip: hideClosed ? 'Afficher les tickets fermés' : 'Masquer les tickets fermés',
        onClick: () => setHideClosed(v => !v),
    }], [status, hideClosed, reload])

    const ticketsFiltered = useMemo(() => {
        const { searchValue, category, status: statusFilter, reporter, handler } = filter
        const bySearch = searchValue
            ? tickets.filter(t => `${t.id} ${t.summary ?? ''}`.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()))
            : tickets
        const byCategory = category?.length ? bySearch.filter(t => category.includes(t.category)) : bySearch
        const byStatus = statusFilter?.length ? byCategory.filter(t => statusFilter.includes(t.status)) : byCategory
        const byReporter = reporter?.length ? byStatus.filter(t => reporter.includes(t.reporter)) : byStatus
        const byHandler = handler?.length ? byReporter.filter(t => handler.includes(t.handler)) : byReporter
        return hideClosed ? byHandler.filter(t => OPEN_STATUS.includes(t.status)) : byHandler
    }, [tickets, filter, hideClosed])

    // Chargement en cours : tant que les tickets se chargent (ou que le projet se résout), on affiche
    // un spinner plutôt que « Aucun ticket ».
    const loading = status === STATUS.LOADING || (status === STATUS.IDLE && (customer?.instances?.length ?? 0) > 0)

    return (
        <TabList
            headerComponent={<FilterTickets tickets={tickets} filter={filter} setFilter={setFilter} />}
            tabs={[
                { constant: TAB_TYPE, label: 'Par type', icon: 'category' },
                { constant: TAB_STATUS, label: 'Par état', icon: 'flag' },
                { constant: TAB_YEAR, label: 'Par année', icon: 'calendar_month' },
            ]}
        >
            {(currentTab) => {
                if (loading) {
                    return (
                        <Grid container justifyContent='center' alignItems='center' sx={{ padding: 3 }}>
                            <CircularProgress />
                        </Grid>
                    )
                }
                if (currentTab === TAB_TYPE) {
                    return <GroupedTables tickets={ticketsFiltered} keyOf={t => t.category ?? 'Sans catégorie'} sortKeys={keys => sortBy(keys)} labelOf={k => k} />
                }
                if (currentTab === TAB_STATUS) {
                    const rank = label => STATUS_TICKETS.find(s => s.value === label)?.sortValue ?? 99
                    return <GroupedTables tickets={ticketsFiltered} keyOf={t => t.status ?? 'Inconnu'} sortKeys={keys => sortBy(keys, rank)} labelOf={k => k} />
                }
                return <GroupedTables tickets={ticketsFiltered} keyOf={t => moment.unix(t.dateSubmitted).year()} sortKeys={keys => keys.sort((a, b) => Number(b) - Number(a))} labelOf={k => k} />
            }}
        </TabList>
    )
}

export default CustomerTicketsApp
